import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslations } from 'use-intl';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCompany } from '@/contexts/CompanyContext';
import { cardsQueryKey, useCards } from '@/hooks/useCards';
import { useCardLabel } from '@/i18n/useCardLabel';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import type { CardInput, CardType, CompanyCard } from '@/types/card';
import { userFacingErrorMessage } from '@/utils/userFacingError';

/** EC2: another register row already shows these digits (and the same card name). */
function hasDuplicate(cards: CompanyCard[], draft: { provider: string; lastFour: string }, editingId?: string): boolean {
  const provider = draft.provider.trim().toLowerCase();
  return cards.some(
    (c) => c.id !== editingId && c.last_four === draft.lastFour && (c.provider ?? '').toLowerCase() === provider,
  );
}

/**
 * FIN-1165 F5/AC6: a consultant keeps the active client company's card register. Scoped to the
 * company selected in the consultant app (not employee-api's implicit one): card-api takes the id.
 */
export const CardSettings = () => {
  const t = useTranslations('consultantCards.settings');
  const shared = useTranslations('consultantCards');
  const cardLabel = useCardLabel();
  const { company } = useCompany();
  const companyId = company?.id;
  const queryClient = useQueryClient();
  const cardService = ServiceRegistry.getCardService();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CompanyCard | null>(null);
  const [removeTarget, setRemoveTarget] = useState<CompanyCard | null>(null);

  const register = useCards(companyId);

  const refresh = () => queryClient.invalidateQueries({ queryKey: cardsQueryKey(companyId) });

  const save = useMutation({
    mutationFn: (input: CardInput) =>
      editing
        ? cardService.updateCard(companyId as string, editing.id, input)
        : cardService.createCard(companyId as string, input),
    onSuccess: async () => {
      toast.success(editing ? t('cardUpdated') : t('cardAdded'));
      setFormOpen(false);
      await refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (cardId: string) => cardService.deleteCard(companyId as string, cardId),
    onSuccess: async () => {
      toast.success(t('cardRemoved'));
      await refresh();
    },
    onError: (error) => toast.error(userFacingErrorMessage(error, t('removeFailed'))),
    onSettled: () => setRemoveTarget(null),
  });

  const cards = register.data?.cards ?? [];

  const openAdd = () => {
    save.reset();
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (card: CompanyCard) => {
    save.reset();
    setEditing(card);
    setFormOpen(true);
  };

  const renderBody = () => {
    if (!companyId) {
      return <p className="text-sm text-muted-foreground py-4">{t('selectCompany')}</p>;
    }
    if (register.isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (register.isError) {
      return (
        <Alert variant="destructive">
          <AlertTitle>{t('loadFailedTitle')}</AlertTitle>
          <AlertDescription>
            {userFacingErrorMessage(register.error, t('loadFailedFallback'))}
            <div className="mt-2">
              <Button size="sm" variant="outline" onClick={() => register.refetch()}>{shared('tryAgain')}</Button>
            </div>
          </AlertDescription>
        </Alert>
      );
    }
    if (cards.length === 0) {
      return (
        <p className="text-sm text-muted-foreground py-4">{t('empty')}</p>
      );
    }
    return (
      <ul className="divide-y">
        {cards.map((card) => (
          <li key={card.id} className="flex items-center justify-between gap-3 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">{cardLabel(card)}</p>
              <p className="text-xs text-muted-foreground">
                {card.holderName
                  ? t('holderLine', { holder: card.holderName, type: card.card_type })
                  : t('noHolder')}
              </p>
            </div>
            <div className="flex flex-none items-center gap-1">
              {!card.holderName && <Badge variant="destructive">{t('notMatching')}</Badge>}
              <Button variant="ghost" size="icon" aria-label={t('changeLabel', { card: cardLabel(card) })} onClick={() => openEdit(card)}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" aria-label={t('removeLabel', { card: cardLabel(card) })} onClick={() => setRemoveTarget(card)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              {t('title')}
            </CardTitle>
            <CardDescription>{t('description')}</CardDescription>
          </div>
          {register.isSuccess && (
            <Button size="sm" onClick={openAdd}>
              <Plus className="h-4 w-4 mr-1" />
              {t('add')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {renderBody()}
      </CardContent>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? t('changeTitle') : t('add')}</DialogTitle>
          </DialogHeader>
          {formOpen && (
            <CardForm
              key={editing?.id ?? 'new'}
              card={editing}
              cards={cards}
              employees={register.data?.employees ?? []}
              pending={save.isPending}
              error={save.isError ? userFacingErrorMessage(save.error, t('saveFailedFallback')) : null}
              onCancel={() => setFormOpen(false)}
              onSave={(input) => save.mutate(input)}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!removeTarget} onOpenChange={(open) => { if (!open) setRemoveTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('removeTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('removeBody', { card: removeTarget ? cardLabel(removeTarget) : '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('keep')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={remove.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (removeTarget) remove.mutate(removeTarget.id);
              }}
            >
              {remove.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              {t('remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

function CardForm({ card, cards, employees, pending, error, onCancel, onSave }: {
  card: CompanyCard | null;
  cards: CompanyCard[];
  employees: Array<{ id: string; name: string; hasLogin: boolean }>;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (input: CardInput) => void;
}) {
  const t = useTranslations('consultantCards.settings.form');
  const [provider, setProvider] = useState(card?.provider ?? '');
  const [lastFour, setLastFour] = useState(card?.last_four ?? '');
  const [cardType, setCardType] = useState<CardType>(card?.card_type ?? 'personal');
  const [holderId, setHolderId] = useState(card?.holder_employee_id ?? '');

  const lastFourValid = /^\d{4}$/.test(lastFour);
  const duplicate = hasDuplicate(cards, { provider, lastFour }, card?.id);

  return (
    <>
      <div className="space-y-4 py-2">
        <div className="space-y-2">
          <Label htmlFor="card-provider">{t('name')}</Label>
          <Input
            id="card-provider"
            value={provider}
            maxLength={40}
            placeholder={t('namePlaceholder')}
            onChange={(e) => setProvider(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="card-last-four">{t('lastFour')}</Label>
          <Input
            id="card-last-four"
            value={lastFour}
            inputMode="numeric"
            maxLength={4}
            placeholder="1234"
            aria-invalid={lastFour !== '' && !lastFourValid}
            onChange={(e) => setLastFour(e.target.value.replace(/\D/g, '').slice(0, 4))}
          />
          {lastFour !== '' && !lastFourValid && (
            <p className="text-xs text-destructive">{t('fourDigits')}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label id="card-type-label">{t('whoseMoney')}</Label>
          <RadioGroup aria-labelledby="card-type-label" value={cardType} onValueChange={(v) => setCardType(v === 'company' ? 'company' : 'personal')}>
            <div className="flex items-center gap-2">
              <RadioGroupItem id="card-type-personal" value="personal" />
              <Label htmlFor="card-type-personal" className="font-normal">{t('personal')}</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem id="card-type-company" value="company" />
              <Label htmlFor="card-type-company" className="font-normal">{t('company')}</Label>
            </div>
          </RadioGroup>
        </div>
        <div className="space-y-2">
          <Label>{t('holder')}</Label>
          <Select value={holderId} onValueChange={setHolderId}>
            <SelectTrigger aria-label={t('holder')}>
              <SelectValue placeholder={t('chooseEmployee')} />
            </SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.hasLogin ? e.name : t('noLogin', { name: e.name })}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {employees.length === 0 && (
            <p className="text-xs text-muted-foreground">{t('noEmployees')}</p>
          )}
        </div>
        {duplicate && (
          <Alert>
            <AlertTitle>{t('duplicateTitle')}</AlertTitle>
            <AlertDescription>{t('duplicateBody')}</AlertDescription>
          </Alert>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertTitle>{t('saveErrorTitle')}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" disabled={pending} onClick={onCancel}>{t('cancel')}</Button>
        <Button
          disabled={!lastFourValid || holderId === '' || pending}
          onClick={() =>
            onSave({
              provider: provider.trim() === '' ? null : provider.trim(),
              lastFour,
              cardType,
              holderEmployeeId: holderId,
            })
          }
        >
          {pending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
          {t('save')}
        </Button>
      </DialogFooter>
    </>
  );
}
